import { Fragment, useEffect } from "react";
import { VisualGroupHeading } from "../VisualGroupHeading";
import { groupHeading } from "../visualGroups";
import { ActionButton, ChoiceControl } from "../../components/ui";
import AutoCompleteDropdown from "../../components/ui/AutoCompleteDropdown";
import { optionImageStyle } from "../../components/ui/AutoCompleteDropdown/style";
import {
  ChoiceInstrument,
  DropdownInstrument,
} from "../../components/Wrappers/ChoiceInstrument";
import { useThemeContext } from "../../globalContext/global-context";
import type { QuestionViewProps } from "../contracts";
import type { SelectDefinition, SelectDraft, SelectEvent } from "./core";

export type SelectViewProps = QuestionViewProps<
  SelectDefinition,
  SelectDraft,
  SelectEvent
>;

const Choices = (props: SelectViewProps) => {
  const { arrangement } = props.display;
  const isList = props.display.view === "list";
  const onlyOption =
    props.definition.options.length === 1
      ? props.definition.options[0]
      : undefined;
  useEffect(() => {
    if (!onlyOption || onlyOption.disabled) return;
    const selectOnlyOption = () => {
      const selection: SelectEvent = {
        type: "select",
        optionId: onlyOption.id,
      };
      if (onlyOption.customPrompt !== undefined) {
        props.controls.specify?.open({
          kind: "option-answer",
          optionId: onlyOption.id,
        });
      } else props.submit(selection);
    };
    props.registerSuggestedAction?.(selectOnlyOption);
    return () => props.registerSuggestedAction?.(null);
  }, [
    onlyOption,
    props.controls.specify,
    props.registerSuggestedAction,
    props.submit,
  ]);

  return (
    <ChoiceInstrument arrangement={arrangement} isList={isList}>
      {props.definition.options.map((option, index) => {
        const event: SelectEvent = { type: "select", optionId: option.id };
        const handleSelect = () => {
          if (option.customPrompt !== undefined) {
            props.controls.specify?.open({
              kind: "option-answer",
              optionId: option.id,
            });
          } else props.submit(event);
        };
        const currentVal = {
          value: option.id,
          text: option.label,
          disabled: option.disabled,
          display_text: option.displayLabel,
          image: option.image,
        };
        const common = {
          currentVal,
          adaptiveText: { maxLines: 3 },
          disabled: option.disabled,
          isSelected: props.draft.selectedOptionId === option.id,
          onClick: handleSelect,
          testId: `send-response ${index}`,
        };

        const choice = isList ? (
          <ChoiceControl
            {...common}
            mode="radio"
            buttonImageStyle={optionImageStyle}
            controlAriaLabel={option.label}
            controlName="select-choice"
            controlValue={String(option.id)}
            sx={{ textAlign: "start" }}
          />
        ) : (
          <ActionButton
            {...common}
            ariaLabel={option.label}
            ariaPressed={props.draft.selectedOptionId === option.id}
            sx={{ cursor: option.disabled ? "not-allowed" : "pointer" }}
          />
        );
        return (
          <Fragment key={option.id}>
            <VisualGroupHeading
              text={groupHeading(
                props.definition.optionGroups,
                option.id as number,
              )}
            />
            {choice}
          </Fragment>
        );
      })}
    </ChoiceInstrument>
  );
};

export const SelectButtonsView = (props: SelectViewProps) => (
  <Choices {...props} />
);

export const SelectListView = (props: SelectViewProps) => (
  <Choices {...props} />
);

export const SelectDropdownView = (props: SelectViewProps) => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const options = props.definition.options.map((option) => ({
    value: option.id,
    text: option.label,
    disabled: option.disabled,
    display_text: option.displayLabel,
    image: option.image,
  }));
  return (
    <DropdownInstrument>
      <AutoCompleteDropdown
        testId="select-input"
        options={options}
        groups={props.definition.optionGroups}
        value={
          options.find(
            (option) => option.value === props.draft.selectedOptionId,
          ) ?? null
        }
        onSelect={(option) => {
          const selected = Array.isArray(option) ? option[0] : option;
          if (!selected || !props.controls.canSubmit) return;
          const event: SelectEvent = {
            type: "select",
            optionId: selected.value,
          };
          if (
            props.definition.options.some(
              (choice) =>
                choice.id === selected.value &&
                choice.customPrompt !== undefined,
            )
          ) {
            props.controls.specify?.open({
              kind: "option-answer",
              optionId: selected.value,
            });
          } else props.submit(event);
        }}
        onClear={() => {
          if (props.controls.canSubmit) props.dispatch({ type: "clear" });
        }}
        clearDisabled={!props.controls.canSubmit}
        primaryColor={theme.tokens.color.action.primary}
        inputBgColor={theme.tokens.color.surface.card}
      />
    </DropdownInstrument>
  );
};
